import React from 'react';
import { NodeStruct, NodeCategory } from '@/types/node-types';
import {
  getCategoryConfig,
  getNodeIconColor,
  GREY_COLOR,
} from '@/constants/node-styling';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { IconTile } from '../../icon-tile';

/** Props for ConnectedNodeStatus */
export interface ConnectedNodeStatusProps {
  connectedNodes: NodeStruct[];
  config: ReturnType<typeof getCategoryConfig>;
  category: string;
  portDirection: 'input' | 'output';
}

/** Displays connected node icons with overlap, or default icon when no connections */
export const ConnectedNodeStatus: React.FC<ConnectedNodeStatusProps> = ({
  connectedNodes,
  config,
  category,
  portDirection,
}) => {
  const connectionCount = connectedNodes.length;

  // Helper function to format the connection label
  const getConnectionLabel = () => {
    if (connectionCount === 0) {
      return config.title;
    }

    // Format connected node names
    const nodeNames = connectedNodes
      .map((node) => node.user_config_name || node.display_name)
      .join(', ');

    // For Tools
    if (category === NodeCategory.TOOL) {
      const title =
        connectionCount === 1 ? config.title.replace(/s$/, '') : config.title;
      return `Using ${connectionCount} ${title}`;
    }

    // For Input
    if (portDirection === 'input') {
      return `Receiving ${config.title} from ${nodeNames}`;
    }

    // For Output
    if (portDirection === 'output') {
      return `Sending ${config.title} to ${nodeNames}`;
    }

    // Fallback (should not reach here)
    return `${connectionCount} ${connectionCount === 1 ? config.title.replace(/s$/, '') : config.title}`;
  };

  // No connections - show default icon with grey color
  if (connectionCount === 0) {
    return (
      <>
        <IconTile
          icon={config.icon || ''}
          backgroundColor={GREY_COLOR}
          iconColor='#ffffff'
          size='small'
          iconClassName='!w-5 !h-5'
          style={{
            background: `linear-gradient(to bottom, ${GREY_COLOR}, color-mix(in srgb, ${GREY_COLOR} 75%, black) )`,
          }}
        />
        <TruncateTextWithTooltip
          text={config.title}
          className='text-sm font-medium pl-1 min-w-0 dark:text-white!'
        />
      </>
    );
  }

  // Has connections - show overlapping icons and count
  const maxDisplayIcons = 5;
  const displayNodes = connectedNodes.slice(0, maxDisplayIcons);
  const overlapOffset = 20; // pixels to overlap each icon

  return (
    <>
      {/* Overlapping icon squares */}
      <div
        className='flex items-center shrink-0 relative'
        style={{
          width: `${32 + (displayNodes.length - 1) * overlapOffset}px`,
          height: '32px',
        }}
      >
        {displayNodes.map((node, index) => {
          const iconColor =
            getNodeIconColor(node.node_type, node.icon_color) ||
            config.handleStyling.color;
          return (
            <IconTile
              key={`${node.node_id}-${index}`}
              icon={node.icon || 'Cable'}
              backgroundColor={iconColor}
              iconColor='#ffffff'
              size='small'
              iconClassName='!w-5 !h-5'
              className={`absolute ${index > 0 ? 'shadow-[-4px_0px_10px_-4px_rgba(0,0,0,0.8)]' : ''}`}
              style={{
                left: `${index * overlapOffset}px`,
                zIndex: index,
                background: `linear-gradient(to bottom, ${iconColor}, color-mix(in srgb, ${iconColor} 90%, black) )`,
              }}
            />
          );
        })}
      </div>
      <TruncateTextWithTooltip
        text={getConnectionLabel()}
        className='text-sm font-medium pl-1 min-w-0 dark:text-white!'
        tooltipProps={{
          side: portDirection === 'output' ? 'left' : 'right',
          align: 'start',
          sideOffset: portDirection === 'output' ? 50 : 80,
          className: 'max-w-xs',
        }}
      />
    </>
  );
};
